import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { COLLATERAL_API_SERVICE_TOKEN, CollateralApiService } from '@nk/api-lib';
import { CompanyCollateralComposite, SimpleCollection } from '@nk/data-entities-lib';
import { REPORT_ENTITIES_LIMIT } from '@nk/report-block';
import { PaginationComponent, ProgressComponent, ThousandSeparatedPipe } from '@nk/ui-lib';
import { DateFormats, LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { PrettyCompanyNamePipe } from '../../pipes/pretty-company-name.pipe';
import { CompanyTitleComponent } from '../company-title/company-title.component';
import { CollateralItemsComponent } from './collateral-items.component';

interface Options {
  showForCorporateGroup: boolean;
  includeDeletedCollateral: boolean;
  limit: number;
  offset: number;
}

@Component({
  selector: 'nk-company-collateral-table',
  templateUrl: './company-collateral-table.component.html',
  imports: [
    AsyncPipe,
    DatePipe,
    RouterModule,
    TranslateModule,
    TooltipModule,
    ProgressComponent,
    PaginationComponent,
    CompanyTitleComponent,
    CollateralItemsComponent,
    PrettyCompanyNamePipe,
    ThousandSeparatedPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyCollateralTableComponent implements OnInit {
  public companyUuid = input.required<string>();
  public showForCorporateGroup = input(false);
  public includeDeletedCollateral = input(false);
  public printVersion = input(false);
  public limit = 10;
  public currentPage = signal(0);

  public printLimit = REPORT_ENTITIES_LIMIT;
  public collateralComposite$!: Observable<SimpleCollection<CompanyCollateralComposite>>;
  public dateFormats = DateFormats;
  private _collateralService = inject<CollateralApiService>(COLLATERAL_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _optionsChange = new BehaviorSubject<Options>(null);
  public loading = signal(false);

  constructor() {
    effect(() => {
      this._optionsChange.next({
        showForCorporateGroup: this.showForCorporateGroup(),
        includeDeletedCollateral: this.includeDeletedCollateral(),
        limit: this.printVersion() ? REPORT_ENTITIES_LIMIT : this.limit,
        offset: this.currentPage() * this.limit,
      });
    });
  }

  public ngOnInit(): void {
    this.collateralComposite$ = this._optionsChange.pipe(
      tap(() => {
        this._reportLoadingStateService?.setLoadingState(this.constructor.name, true);
        this.loading.set(true);
      }),
      debounceTime(300),
      filter((options) => options !== null),
      switchMap((options) => {
        return this._collateralService.companyCollateralCompositeCollection.get(
          this.companyUuid(),
          options.showForCorporateGroup,
          !options.includeDeletedCollateral, // This is inverted
          options.limit,
          options.offset,
        );
      }),
      tap(() => {
        this._reportLoadingStateService?.setLoadingState(this.constructor.name, false);
        this.loading.set(false);
      }),
    );
  }

  public goToPage(page: number): void {
    this.currentPage.set(page);
  }
}
